import $ from "jquery";
import {gsap} from "gsap/all";
import {cloneSvg, setFillColors} from "../SvgBank";
import {CircleTimer} from "../CircleTimer";

// The two little previews of screen 1, one per instruction: a timing press
// hitting its window and a memory sequence being entered. They play the same
// visuals as the real actions, on a loop, with nobody pressing anything.
export class ActionDemo {

  constructor($host, color) {
    this.$host = $host;
    this.color = color;

    // A loop keeps going as long as this is on, so a stop cuts it even where
    // its tweens were already replaced by the next iteration.
    this.running = false;

    // --- Timing ---
    this.successRatio = 0.79;
    this.windowRatio = 0.075;
    this.shrinkDuration = 2.5;
    this.holdSec = 1;
    this.pauseSec = 0.6;

    this.$press = null;
    this.tween = null;
    this.holdCall = null;
    this.pauseCall = null;

    // --- Memory ---
    this.slotPressHoldSec = 0.9;
    this.slotFillDelaySec = 0.9 + this.slotPressHoldSec;
    this.memoryHoldSec = 1.2 + this.slotPressHoldSec;
    this.memoryPauseSec = 0.9;

    this.showDurationSec = 1.4;
    this.showOutSec = 0.4;

    this.$slots = null;
    this.slotsTween = null;
    this.memoryTimeline = null;
    this.timer = null;

    // The waits inside a memory loop, kept so a stop can cut them short.
    this.slotCalls = [];
  }

  // --- Timing preview (instruction1) ---------------------------------
  buildTiming() {
    const $presses = $(`
      <div class="timing-presses">
        <div class="timing-press stacked-button demo">
          <div class="base"></div>
          <div class="state success"></div>
          <div class="state fail"></div>
        </div>
      </div>
    `);

    const $press = $presses.find(".timing-press");

    const $base = cloneSvg("timing-button");
    $base.find("circle[stroke]:not([stroke-dasharray])").attr("class", "ring");
    $base.find("circle[stroke-dasharray]").attr("class", "zone-outline");
    $base.find('circle[fill="black"]').attr("class", "zone");
    const $inner = $base.find('circle[fill^="url("]').attr("class", "inner");
    if (this.color) setFillColors($base, $inner, this.color);

    $press.find(".base").append($base);
    $press.find(".state.success").append(cloneSvg("button-success"));
    $press.find(".state.fail").append(cloneSvg("button-error"));

    this.$host.empty().append($presses);
    this.$press = $press;
  }

  // Both starts stop whatever was already looping first, so a second start
  // never leaves a loop of its own running next to the new one.
  startTiming() {
    this.stop();
    this.running = true;
    this.loopTiming();
  }

  loopTiming() {
    if (!this.running) return;

    this.$press.removeClass("success fail in-window");
    gsap.set(this.$press.find(".ring"), {scale: 1, transformOrigin: "50% 50%"});

    this.tween = gsap.timeline();
    this.tween.to(this.$press.find(".ring"), {
      scale: this.successRatio,
      duration: this.shrinkDuration,
      ease: "none",
    });
    this.tween.add(() => this.$press.addClass("in-window"));
    this.tween.to(this.$press.find(".ring"), {
      scale: this.successRatio - this.windowRatio,
      duration: (this.shrinkDuration / (1 - this.successRatio)) * this.windowRatio,
      ease: "none",
    });
    this.tween.add(() => this.$press.addClass("success"));
    this.tween.add(() => this.$press.removeClass("in-window"), "+=" + this.holdSec);
    this.tween.add(() => this.loopTiming(), "+=" + this.pauseSec);

  }

  // --- Memory preview (instruction2) ----------------------------------
  // outcomes: array of booleans, one per slot, e.g. [true, true, true] or
  // [true, false, true]. Slot count follows the array length.
  buildMemory(actions = ["sq_orange", "sq_blue", "sq_yellow"], outcomes = [true, true, true]) {
    this.actions = actions;
    this.outcomes = outcomes;

    this.$show = $('<div class="memory-show demo"><div class="button"></div></div>');

    const $slots = $('<div class="memory-slots demo"></div>');
    actions.forEach((_, i) => {
      if (i > 0) $slots.append($('<div class="press-arrow"></div>').append(cloneSvg("spacer-arrow")));

      const $slot = $('<div class="memory-slot"><div class="base"></div><div class="fill"></div></div>');
      $slot.find(".base").append(cloneSvg("sequence-slot"));
      $slots.append($slot);
    });

    this.$host.empty().append(this.$show, $slots.hide());
    this.$slots = $slots;

    this.timer = new CircleTimer(this.$host.siblings(".corner-timer"));
  }

  startMemory() {
    this.stop();
    this.running = true;
    this.loopMemory();
  }

  loopMemory() {
    if (!this.running) return;

    this.$slots.find(".memory-slot").removeClass("success fail").find(".fill").empty();
    gsap.set(this.$slots.children(), {autoAlpha: 0, y: 20});
    this.$slots.hide();
    this.$show.show();
    gsap.set(this.$show, {autoAlpha: 0, y: 20});

    const memorizeSec = 0.75 + this.actions.length * this.showDurationSec;
    const executeSec = 0.2 + (this.slotFillDelaySec * this.outcomes.length) + this.memoryHoldSec;

    this.memoryTimeline = gsap.timeline({
      onComplete: () => {
        this.pauseCall = gsap.delayedCall(this.memoryPauseSec, () => this.loopMemory());
      }
    });

    this.memoryTimeline.call(() => this.timer.start(memorizeSec));

    this.actions.forEach((action) => {
      this.memoryTimeline.call(() => this.$show.find(".button").empty().append(cloneSvg(`sequence-button-${action}`)));
      this.memoryTimeline.fromTo(this.$show, {
        autoAlpha: 0,
        y: 20
      }, {
        autoAlpha: 1,
        y: 0,
        duration: 0.4,
        ease: "power2.out"
      });
      this.memoryTimeline.to(this.$show, {
        autoAlpha: 0,
        duration: this.showOutSec,
        ease: "power1.in"
      }, `+=${this.showDurationSec - this.showOutSec}`);
    });

    this.memoryTimeline.call(() => {
      this.$show.hide();
      this.$slots.show();
      this.timer.start(executeSec)
    });

    this.memoryTimeline.to(this.$slots.children(), {
      autoAlpha: 1,
      y: 0,
      duration: 0.4,
      ease: "power2.out",
      stagger: 0.1
    });

    this.outcomes.forEach((success, i) => {
      this.memoryTimeline.call(() => this.fillSlot(i, success), null, `+=${i === 0 ? 0.2 : this.slotFillDelaySec}`);
    });

    this.memoryTimeline.to({}, {duration: this.memoryHoldSec});

    this.memoryTimeline.to(this.$slots.children(), {
      autoAlpha: 0,
      duration: 0.3,
      ease: "power1.in"
    });
  }

  fillSlot(index, success) {
    const $slot = this.$slots.find(".memory-slot").eq(index);
    const action = this.actions[index];

    // Show the button that was "pressed" first, like a real button press...
    $slot.find(".fill").empty().append(cloneSvg(`sequence-button-${action}`));

    // ...then resolve to the success/error mark a beat later.
    this.slotCalls.push(gsap.delayedCall(this.slotPressHoldSec, () => {
      $slot.addClass(success ? "success" : "fail");
      $slot.find(".fill").empty().append(cloneSvg(success ? "sequence-button-success" : "sequence-button-error"));
    }));
  }

  // Stops the loop where it is, leaving the visuals in place. Nothing keeps
  // running once the player has moved on, and startTiming or startMemory picks
  // it up again from the top.
  stop() {
    this.running = false;

    if (this.tween) this.tween.kill();
    if (this.holdCall) this.holdCall.kill();
    if (this.pauseCall) this.pauseCall.kill();
    if (this.slotsTween) this.slotsTween.kill();
    if (this.memoryTimeline) this.memoryTimeline.kill();
    if (this.timer) this.timer.stop();

    this.slotCalls.forEach((call) => call.kill());
    this.slotCalls = [];
  }

  destroy() {
    this.stop();

    if (this.$press) this.$press.remove();
    if (this.$slots) this.$slots.remove();
    if (this.$show) this.$show.remove();
    if (this.timer) {
      this.timer.destroy();
      this.timer = null;
    }
  }
}
